import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { EMPTY_ARRAY_LENGTH, NumberUtils } from '@modeso/aumico-lib-core-fe';
import { ICashFlowFinancingActivities, ICashFlowStatementEntry } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { localizationKeys } from '../../../constants/localizationKeys';
import { CashFlowStatementHelper } from '../../../helpers/cash-flow-statement.helper';
import { ICashFlowFinancingActivitiesComponentInput } from '../../../interfaces/ICashFlowFinancingActivitiesComponentInput.interface';
import { ICashFlowStatementFormEntry } from '../../../interfaces/ICashFlowStatementFormEntry.interface';
const debug = Debug('aumico:aumico-lib-annualreport-fe:CashFlowFinancingActivitiesComponent');
@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-cash-flow-financing-activities-component',
    templateUrl: 'cash-flow-financing-activities.component.html',
    styleUrls: ['cash-flow-financing-activities.component.scss'],
})
export class CashFlowFinancingActivitiesComponent implements OnInit {
    private autoScrollInterval: any = null;
    private lastDragEvent: any = null;

    // refer to finNumber pipe for reference on values
    numberFormat = '1.2-2';

    private financingActivitiesData: ICashFlowFinancingActivitiesComponentInput;
    currentSelectedFliToDeleteIndex: number;
    showDeleteModal: boolean;
    @Input() set financingActivities(value: ICashFlowFinancingActivitiesComponentInput) {
        if (value != null && value.title != null && value.title.trim().length > EMPTY_ARRAY_LENGTH) {
            this.financingActivitiesTitle = value.title;
        }
        if (value && value.flis != null) {
            this.financingActivitiesData = cloneDeep(value);
            this.setTableHeaders();
            if (!this.editMode) {
                this.setInitialTableData();
                this.initializeForm();
            }
        }

        if (this.updateFlisAutomatically && this.financingActivitiesData) {
            this.updateFlisValues();
        }
    }

    private savedFinancingActivitiesData: ICashFlowFinancingActivities;
    @Input() set savedFinancingActivities(value: ICashFlowFinancingActivities) {
        if (value) {
            this.tableEntries = [];
            this.tableHasChanges = false;
            this.editMode = true;
            this.savedFinancingActivitiesData = cloneDeep(value);
            this.setSavedTableData();
            this.initializeForm();
            this.setTitleValue();

            if (this.updateFlisAutomatically) {
                this.updateFlisValues();
            }
        }
    }

    @Input() updateFlisAutomatically: boolean;

    @Input() reportLocale: string;

    form: UntypedFormGroup;

    titleEditMode = false;

    /** Form rows (UI state) */
    tableFormEntries: ICashFlowStatementFormEntry[] = [];

    /** Persisted model rows */
    tableEntries: ICashFlowStatementEntry[] = [];
    private initialTableEntriesData: ICashFlowStatementEntry[] = [];

    private local = localizationKeys;
    currentYearDate: string;
    previousYearDate: string;
    currency: string;

    tableHasChanges = false;

    private editMode = false;

    dataSource: UntypedFormArray;

    currentTotal: number = 0;
    previousTotal: number = 0;

    financingActivitiesTitle = '';

    private formValueChanges: Subscription | undefined;

    @Output() totalChangeEvent = new EventEmitter<{ currentTotal: number; previousTotal: number }>();

    constructor(
        private formBuilder: UntypedFormBuilder,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private localizedTexts: I18NextService,
    ) {
        this.addSvgIcon('aumico-pattern-icon-remove');
        this.addSvgIcon('aumico-pattern-icon-edit-write');
    }

    ngOnInit(): void {
        debug('ngOnInit');
        this.initializeForm();
        this.applyLocalizations();
    }

    private applyLocalizations(): void {
        this.setTitleValue();
    }

    private setTitleValue(): void {
        if (
            !this.editMode &&
            (this.financingActivitiesTitle?.trim().length === EMPTY_ARRAY_LENGTH ||
                this.financingActivitiesTitle == null)
        ) {
            this.financingActivitiesTitle = this.localizedTexts.t(
                this.local.aumico_annual_report_cash_flow_financing_activities_title,
                {
                    lng: this.reportLocale,
                },
            ) as string;
        } else if (
            this.financingActivitiesTitle?.trim().length === EMPTY_ARRAY_LENGTH ||
            this.financingActivitiesTitle == null
        ) {
            this.financingActivitiesTitle = this.savedFinancingActivitiesData.title;
        }
    }

    private setTableHeaders(): void {
        this.currency = this.financingActivitiesData.currency;
        this.currentYearDate = this.financingActivitiesData.currentYearDate;
        this.previousYearDate = this.financingActivitiesData.previousYearDate;
    }

    private setInitialTableData(): void {
        this.tableEntries = [];
        this.prepareTableEntries();
        this.rebuildFormEntriesFromModel();
    }

    private initializeForm(): void {
        this.form = this.formBuilder.group({
            dataSource: this.formBuilder.array([]),
        });
        this.dataSource = this.getAllAsFormArray();
        this.form.setControl('dataSource', this.dataSource);

        // listen to form change
        this.listenToFormChange();
    }

    private updateForm(): void {
        this.dataSource = this.getAllAsFormArray();
        this.form.setControl('dataSource', this.dataSource);
    }

    private rebuildFormEntriesFromModel(): void {
        this.tableFormEntries = (this.tableEntries ?? []).map((e) => {
            const manualCurrent = e.manuallyEnteredCurrentValue ?? null;
            const manualPrevious = e.manuallyEnteredPreviousValue ?? null;
            const systemCurrent = e.calculatedCurrentValue ?? null;
            const systemPrevious = e.calculatedPreviousValue ?? null;

            return {
                name: e.name,
                fliId: e.fliId ?? null,
                isCustom: e.isCustom,
                type: (e as any).type,

                currentValue: manualCurrent !== null ? manualCurrent : systemCurrent,
                previousValue: manualPrevious !== null ? manualPrevious : systemPrevious,

                systemCurrentValue: systemCurrent,
                systemPreviousValue: systemPrevious,

                manuallyEnteredCurrentValue: manualCurrent,
                manuallyEnteredPreviousValue: manualPrevious,
            } as ICashFlowStatementFormEntry;
        });
    }

    private getAllAsFormArray(): UntypedFormArray {
        this.tableFormEntries = this.tableFormEntries.reduce((acc, current) => {
            if (!acc.some((item) => item.fliId === current.fliId) || current.fliId == null) {
                acc.push(current);
            }
            return acc;
        }, []);
        const formGroups: AbstractControl[] = this.tableFormEntries.map((entry) => this.addFormGroup(entry));
        return new UntypedFormArray(formGroups);
    }

    private addFormGroup(dataSource: ICashFlowStatementFormEntry): UntypedFormGroup {
        const dataSourceFormGroup = {};
        Object.keys(dataSource).forEach((key) => {
            dataSourceFormGroup[key] = new UntypedFormControl((dataSource as any)[key]);
        });
        return new UntypedFormGroup(dataSourceFormGroup);
    }

    private listenToFormChange(): void {
        if (this.formValueChanges != null) {
            this.formValueChanges.unsubscribe();
        }

        this.formValueChanges = this.form.valueChanges
            .pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((value) => {
                if (!this.tableHasChanges) {
                    this.tableHasChanges = this.form.dirty;
                }

                // sync UI state
                this.tableFormEntries = cloneDeep<ICashFlowStatementFormEntry[]>(value.dataSource);

                // map back into persisted model for totals + save
                this.tableEntries = this.mapFormEntriesToModelEntries(this.tableFormEntries, this.tableEntries);
                this.setTotals();
            });
    }

    private mapFormEntriesToModelEntries(
        formEntries: ICashFlowStatementFormEntry[],
        existingModelEntries: ICashFlowStatementEntry[],
    ): ICashFlowStatementEntry[] {
        const indexFallback = new Map<string, ICashFlowStatementEntry>();
        (existingModelEntries ?? []).forEach((e) => indexFallback.set(e.fliId ?? e.name, e));

        return (formEntries ?? []).map((fe) => {
            const key = fe.fliId ?? fe.name;
            const base = cloneDeep(indexFallback.get(key)) ?? ({} as ICashFlowStatementEntry);

            const systemCurrent = fe.systemCurrentValue ?? base.calculatedCurrentValue ?? null;
            const systemPrevious = fe.systemPreviousValue ?? base.calculatedPreviousValue ?? null;

            const manualCurrent = this.isManualOverride(fe.currentValue, systemCurrent) ? fe.currentValue : null;
            const manualPrevious = this.isManualOverride(fe.previousValue, systemPrevious) ? fe.previousValue : null;

            return {
                ...base,
                name: fe.name,
                fliId: fe.fliId as any,
                isCustom: fe.isCustom,

                calculatedCurrentValue: systemCurrent,
                calculatedPreviousValue: systemPrevious,

                manuallyEnteredCurrentValue: manualCurrent,
                manuallyEnteredPreviousValue: manualPrevious,
            } as ICashFlowStatementEntry;
        });
    }

    private isManualOverride(value: number | null, systemValue: number | null): boolean {
        if (value === null || value === undefined) {
            return false;
        }
        if (systemValue === null || systemValue === undefined) {
            return true;
        }
        return !NumberUtils.areNumbersEqual(value, systemValue);
    }

    removeAt(index: number): void {
        this.currentSelectedFliToDeleteIndex = index;
        this.showDeleteModal = true;
        debug('remove index', index);
    }

    /** trackBy for the rows *ngFor: reuse DOM nodes by position instead of destroying and recreating
     *  every row on each form rebuild / change-detection pass — essential for large tables. */
    trackByIndex(index: number): number {
        return index;
    }

    /** REV-21417: above this many rows, render via a cdk-virtual-scroll viewport so only the visible
     *  rows are instantiated — a plain table with thousands of rows freezes / OOMs the tab. */
    readonly VIRTUAL_ROW_THRESHOLD = 150;
    /** Fixed row height (px) for the virtual-scroll viewport — MUST match .cfs-vrow height in the SCSS. */
    readonly VIRTUAL_ROW_HEIGHT = 54;

    get isVirtualized(): boolean {
        return (this.dataSource?.controls?.length ?? 0) > this.VIRTUAL_ROW_THRESHOLD;
    }

    onCancelDelete(): void {
        this.currentSelectedFliToDeleteIndex = null;
        this.showDeleteModal = false;
    }

    onDeleteFliRow(): void {
        this.tableHasChanges = true;
        this.tableEntries.splice(this.currentSelectedFliToDeleteIndex, 1);
        this.rebuildFormEntriesFromModel();
        this.updateForm();
        this.currentSelectedFliToDeleteIndex = null;
        this.showDeleteModal = false;
    }

    addRow(): void {
        this.tableHasChanges = true;
        this.tableEntries.push({
            fliId: null,
            name: null,
            calculatedPreviousValue: null,
            calculatedCurrentValue: null,
            manuallyEnteredCurrentValue: null,
            manuallyEnteredPreviousValue: null,
            isCustom: true,
        });
        this.rebuildFormEntriesFromModel();
        this.updateForm();
    }

    private prepareTableEntries(): void {
        if (this.financingActivitiesData != null) {
            const financingActivitiesData: ICashFlowFinancingActivitiesComponentInput =
                cloneDeep<ICashFlowFinancingActivitiesComponentInput>(this.financingActivitiesData);
            this.tableEntries = CashFlowStatementHelper.prepareTableEntriesForFinancingActivities(
                financingActivitiesData.flis,
            );
            this.initialTableEntriesData = cloneDeep<ICashFlowStatementEntry[]>(this.tableEntries);
            this.setTotals();
        }
    }

    private setSavedTableData(): void {
        this.tableEntries = cloneDeep(this.savedFinancingActivitiesData.entries);
        this.initialTableEntriesData = cloneDeep<ICashFlowStatementEntry[]>(this.tableEntries);
        this.financingActivitiesTitle = this.savedFinancingActivitiesData.title;
        this.rebuildFormEntriesFromModel();

        this.setTotals();
    }

    private setTotals(): void {
        const totals: { currentTotal: number; previousTotal: number } =
            CashFlowStatementHelper.calculateTableEntriesTotals(this.tableEntries);
        let currentTotal = 0;
        let previousTotal = 0;
        if (NumberUtils.isFinite(totals.currentTotal)) {
            currentTotal = NumberUtils.roundNumber(totals.currentTotal);
        }

        if (NumberUtils.isFinite(totals.previousTotal)) {
            previousTotal = NumberUtils.roundNumber(totals.previousTotal);
        }
        this.currentTotal = currentTotal;
        this.previousTotal = previousTotal;

        this.totalChangeEvent.emit({ currentTotal: this.currentTotal, previousTotal: this.previousTotal });
    }

    getFinancingActivitiesFormValue(): ICashFlowFinancingActivities {
        return {
            title: this.financingActivitiesTitle,
            entries: CashFlowStatementHelper.mapStringValuesInOperatingAndFinancingTablesToNumbers(
                this.tableEntries,
            ),
        };
    }

    onListDrop(event: CdkDragDrop<ICashFlowStatementEntry[]>): void {
        debug(`Moving item from ${event.previousIndex} to index ${event.currentIndex}`);
        moveItemInArray(this.tableEntries, event.previousIndex, event.currentIndex);
        if (fastDeepEqual(this.initialTableEntriesData, this.tableEntries)) {
            this.tableHasChanges = false;
        } else {
            this.tableHasChanges = true;
        }

        this.rebuildFormEntriesFromModel();
        this.updateForm();
    }

    onSaveTitle($event: string): void {
        this.financingActivitiesTitle = $event;
        this.tableHasChanges = true;
    }

    private updateFlisValues(): void {
        if (this.financingActivitiesData) {
            this.tableEntries = CashFlowStatementHelper.updateFinancingActivitiesFlisValuesWithShadowCopy(
                this.tableEntries,
                this.financingActivitiesData,
            );
            this.rebuildFormEntriesFromModel();
        }

        this.updateForm();
        this.setTotals();
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    /// Drag and Drop Methods ///

    onRowDragMoved(event: any) {
        this.lastDragEvent = event;
        const edgeThreshold = 60; // px from top/bottom to start scrolling
        const scrollSpeed = 20; // px per interval
        const pointerY = event.pointerPosition.y;
        // Find the scrollable parent of the table
        const tableElement = (event.source && event.source.element) ? event.source.element.nativeElement : null;
        let scrollContainer = null;
        if (tableElement) {
            scrollContainer = this.findScrollableParent(tableElement);
        }
        if (!scrollContainer) {
            scrollContainer = document.getElementById('mainscrollcontent');
        }
        if (!scrollContainer) return;
        const rect = scrollContainer.getBoundingClientRect();

        // Scroll up
        if (pointerY < rect.top + edgeThreshold) {
            this.startAutoScroll(-scrollSpeed, scrollContainer, event);
        }
        // Scroll down
        else if (pointerY > rect.bottom - edgeThreshold) {
            this.startAutoScroll(scrollSpeed, scrollContainer, event);
        } else {
            this.stopAutoScroll();
        }
    }

    startAutoScroll(delta: number, scrollContainer: HTMLElement, dragEvent?: any) {
        if (this.autoScrollInterval) return;
        this.autoScrollInterval = setInterval(() => {
            scrollContainer.scrollTop += delta;
            // Simulate mousemove event at current pointer position
            if (dragEvent && dragEvent.pointerPosition) {
                const evt = new MouseEvent('mousemove', {
                    clientX: dragEvent.pointerPosition.x,
                    clientY: dragEvent.pointerPosition.y,
                    bubbles: true
                });
                document.dispatchEvent(evt);
            }
        }, 16); // ~60fps
    }

    stopAutoScroll() {
        if (this.autoScrollInterval) {
            clearInterval(this.autoScrollInterval);
            this.autoScrollInterval = null;
        }
    }

    onRowDragEnded() {
        this.stopAutoScroll();
        this.lastDragEvent = null;
    }

    // Utility to find scrollable parent
    private findScrollableParent(element: HTMLElement): HTMLElement | null {
        let parent = element.parentElement;
        while (parent) {
            if (this.isScrollable(parent)) {
                return parent;
            }
            parent = parent.parentElement;
        }
        return null;
    }

    private isScrollable(element: HTMLElement): boolean {
        const overflowY = window.getComputedStyle(element).overflowY;
        return (overflowY === 'scroll' || overflowY === 'auto') && element.scrollHeight > element.clientHeight;
    }
}
